import { useState, useEffect, useRef } from 'react'
import { i18n } from '@fs/zion-locale'
import { getDynamicLinks } from './getDynamicLinks'

// Helper: convert flag overrides to csv (flag1,flag2,!flag3)
function buildForceFeatureFlagsCsv(overrides) {
  if (!overrides || typeof overrides !== 'object') return ''
  const entries = Object.entries(overrides)
  if (!entries.length) return ''
  return entries
    .map(([name, override]) => {
      const treatment = (override?.treatment || '').toString().toLowerCase()
      return treatment === 'off' ? `!${name}` : name
    })
    .filter(Boolean)
    .join(',')
}

// Helper: read overrides from localStorage (fsZionFlags)
function getLocalFlagOverrides() {
  if (typeof window === 'undefined') return {}
  const raw = window.localStorage?.getItem('fsZionFlags')
  if (!raw) return {}
  try {
    return JSON.parse(raw) || {}
  } catch (e) {
    throw new Error(`Invalid JSON in localStorage key "fsZionFlags": ${e.message}`)
  }
}

// Helper: merge existing & override forceFeatureFlags
function mergeForceFeatureFlags(params, overridesCsv) {
  if (!overridesCsv) return params
  const existing = params.get('forceFeatureFlags') || ''
  if (!existing) {
    params.set('forceFeatureFlags', overridesCsv)
    return params
  }
  const toTokenMap = (csv) =>
    csv
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .reduce((acc, token) => {
        const base = token.replace(/^!/, '')
        if (!acc.has(base)) acc.set(base, token)
        return acc
      }, new Map())
  const existingMap = toTokenMap(existing)
  const overrideTokens = overridesCsv
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
  overrideTokens.forEach((token) => {
    const base = token.replace(/^!/, '')
    if (!existingMap.has(base)) existingMap.set(base, token)
  })
  params.set('forceFeatureFlags', Array.from(existingMap.values()).join(','))
  return params
}

function stringsToBoolean(value) {
  if (typeof value === 'object' && !Array.isArray(value)) {
    return Object.keys(value).reduce((obj, key) => ({ ...obj, [key]: stringsToBoolean(value[key]) }), {})
  }
  if (Array.isArray(value)) {
    return value.map(stringsToBoolean)
  }
  return value === 'true' || value === 'false' ? value === 'true' : value
}

export default function useDynamicNavigation() {
  const [state, setState] = useState({ loading: false })
  const [queryParams, setQueryParams] = useState({})
  const mounted = useRef(true)
  // Helpers moved outside hook for testability & lower complexity

  // Function to update query params and trigger a refresh
  const setDynamicQueryParams = (newParams) => {
    setQueryParams(newParams)
    // Reset navigation to trigger a fresh fetch with new params
    setState((prevState) => ({ ...prevState, navigation: null, error: null }))
  }

  // If language changes we need to refresh the navigation - needs to run on every render to catch changes
  const [language, setLanguage] = useState(i18n.language)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (language !== i18n.language) {
      setLanguage(i18n.language)
      setState({ loading: false })
    }
  })

  useEffect(() => {
    mounted.current = true
    if (!state.error && !state.loading && !state.navigation) {
      setState({ loading: true })
      let overridesCsv = ''
      try {
        overridesCsv = buildForceFeatureFlagsCsv(getLocalFlagOverrides())
      } catch (e) {
        if (mounted.current) {
          setState({ loading: false, error: e })
        }
        return () => {}
      }
      const params = new URLSearchParams(queryParams)
      const finalParams = mergeForceFeatureFlags(params, overridesCsv)
      getDynamicLinks(finalParams)
        .then((newNavigation) => {
          if (mounted.current) {
            setState({
              loading: false,
              navigation: stringsToBoolean(newNavigation),
            })
          }
        })
        .catch((error) => {
          if (mounted.current) {
            setState({ loading: false, error })
          }
        })
    }
    return () => {
      mounted.current = false
    }
  }, [state.error, state.loading, state.navigation, queryParams])

  return { ...state, setDynamicQueryParams }
}
